import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight,
  User,
  Phone,
  Mail,
  MapPin,
  FileText,
  DollarSign,
  Calendar,
  Edit,
  Trash2,
  Printer,
  Send,
  Plus,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  History,
  ExternalLink
} from 'lucide-react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';

const InvoiceDetailsPage = () => {
  const navigate = useNavigate();
  const { invoiceId } = useParams();
  const { hasPermission, user } = useAdminAuth();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  // Mock data - سيتم استبداله بـ API
  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setInvoice({
        id: 'INV-2024-001',
        clientId: 'CL-2024-001',
        clientName: 'محمد أحمد',
        clientType: 'فرد',
        clientPhone: '+966 50 123 4567',
        clientEmail: 'mohamed.ahmed@example.com',
        clientAddress: 'الرياض، حي الملز، شارع الأمير محمد بن عبدالعزيز',
        serviceType: 'استشارة قانونية',
        caseId: 'WZ-2024-001',
        status: 'paid',
        paymentMethod: 'بطاقة ائتمانية',
        issueDate: '2024-12-01',
        dueDate: '2024-12-15',
        items: [
          {
            id: 1,
            name: 'استشارة قانونية - جلسة واحدة',
            description: 'استشارة قانونية شاملة حول القضية رقم WZ-2024-001',
            price: 3000,
            quantity: 1,
            total: 3000
          },
          {
            id: 2,
            name: 'رسوم إدارية',
            description: 'رسوم إدارية ومعالجة',
            price: 500,
            quantity: 1,
            total: 500
          }
        ],
        subtotal: 3500,
        discount: 0,
        discountPercentage: 0,
        tax: 525, // 15% VAT
        taxPercentage: 15,
        totalAmount: 4025,
        paidAmount: 4025,
        remainingBalance: 0,
        transactions: [
          {
            id: 'TRX-001',
            date: '2024-12-05',
            method: 'بطاقة ائتمانية',
            amount: 4025,
            balanceAfter: 0,
            processedBy: 'أحمد المحاسب'
          }
        ],
        notes: 'تم استلام المبلغ كاملاً في الموعد المحدد',
        auditLog: [
          {
            id: 1,
            action: 'إنشاء فاتورة',
            user: 'أحمد المحاسب',
            date: '2024-12-01 10:30',
            details: 'تم إنشاء الفاتورة بمبلغ 4,025 ر.س'
          },
          {
            id: 2,
            action: 'إرسال للعميل',
            user: 'أحمد المحاسب',
            date: '2024-12-01 10:35',
            details: 'تم إرسال الفاتورة للعميل عبر البريد الإلكتروني'
          },
          {
            id: 3,
            action: 'تسجيل دفعة',
            user: 'أحمد المحاسب',
            date: '2024-12-05 14:20',
            details: 'تم تسجيل دفعة بمبلغ 4,025 ر.س - بطاقة ائتمانية'
          },
          {
            id: 4,
            action: 'تحديث الحالة',
            user: 'النظام',
            date: '2024-12-05 14:20',
            details: 'تم تحديث حالة الفاتورة إلى "مدفوعة"'
          }
        ],
        createdBy: 'أحمد المحاسب',
        createdAt: '2024-12-01 10:30',
        updatedAt: '2024-12-05 14:20'
      });
      setLoading(false);
    }, 500);
  }, [invoiceId]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      paid: {
        label: 'مدفوعة',
        icon: CheckCircle,
        className: 'bg-green-50 text-green-700 border-green-200'
      },
      pending: {
        label: 'معلقة',
        icon: Clock,
        className: 'bg-yellow-50 text-yellow-700 border-yellow-200'
      },
      overdue: {
        label: 'متأخرة',
        icon: AlertCircle,
        className: 'bg-red-50 text-red-700 border-red-200'
      },
      cancelled: {
        label: 'ملغاة',
        icon: XCircle,
        className: 'bg-gray-50 text-gray-700 border-gray-200'
      }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold border-2 ${config.className}`}>
        <Icon className="w-5 h-5" />
        {config.label}
      </span>
    );
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSendToClient = () => {
    alert('سيتم إرسال الفاتورة للعميل عبر البريد الإلكتروني');
  };

  const handleRecordPayment = () => {
    navigate(`/admin/finance/invoices/${invoiceId}/payment`);
  };

  const handleEdit = () => {
    navigate(`/admin/finance/invoices/${invoiceId}/edit`);
  };

  const handleDelete = () => {
    if (window.confirm('هل أنت متأكد من حذف هذه الفاتورة؟ هذا الإجراء لا يمكن التراجع عنه.')) {
      // Handle delete
      alert('تحتاج صلاحيات خاصة لحذف الفاتورة');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">جاري تحميل تفاصيل الفاتورة...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!invoice) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">الفاتورة غير موجودة</p>
            <button
              onClick={() => navigate('/admin/finance/invoices')}
              className="mt-4 px-6 py-2 bg-[#1E3A8A] text-white rounded-lg"
            >
              العودة للفواتير
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/admin/finance/invoices')}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-[#1E3A8A] mb-6 transition-colors"
        >
          <ArrowRight className="w-5 h-5" />
          <span className="font-semibold">العودة للفواتير</span>
        </button>

        {/* 1️⃣ Header Section */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-[#1E3A8A] rounded-lg flex items-center justify-center">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-black text-[#1E3A8A]">{invoice.id}</h1>
                <p className="text-gray-600">فاتورة {invoice.serviceType}</p>
              </div>
            </div>
            {getStatusBadge(invoice.status)}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <User className="w-4 h-4 text-[#1E3A8A]" />
                <p className="text-sm text-gray-600">العميل</p>
              </div>
              <p className="font-bold text-gray-800">{invoice.clientName}</p>
              <p className="text-xs text-gray-500">{invoice.clientType}</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-[#1E3A8A]" />
                <p className="text-sm text-gray-600">تاريخ الإصدار</p>
              </div>
              <p className="font-bold text-gray-800">{invoice.issueDate}</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-red-600" />
                <p className="text-sm text-gray-600">تاريخ الاستحقاق</p>
              </div>
              <p className="font-bold text-gray-800">{invoice.dueDate}</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-4 h-4 text-green-600" />
                <p className="text-sm text-gray-600">المبلغ الإجمالي</p>
              </div>
              <p className="font-bold text-green-600 text-xl">{invoice.totalAmount.toLocaleString()} ر.س</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* 2️⃣ Client Information */}
            <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-black text-[#1E3A8A]">بيانات العميل</h2>
                <button
                  onClick={() => navigate(`/admin/crm/clients/${invoice.clientId}`)}
                  className="inline-flex items-center gap-2 text-sm text-[#1E3A8A] hover:text-[#C8A870] transition-colors"
                >
                  <span>عرض الملف الكامل</span>
                  <ExternalLink className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">الاسم</p>
                    <p className="font-semibold text-gray-800">{invoice.clientName}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">الهاتف</p>
                    <p className="font-semibold text-gray-800">{invoice.clientPhone}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">البريد الإلكتروني</p>
                    <p className="font-semibold text-gray-800">{invoice.clientEmail}</p>
                  </div>
                </div>

                {invoice.clientAddress && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gray-400 mt-1" />
                    <div>
                      <p className="text-sm text-gray-600">العنوان</p>
                      <p className="font-semibold text-gray-800">{invoice.clientAddress}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 3️⃣ Service Details */}
            <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
              <h2 className="text-xl font-black text-[#1E3A8A] mb-4">تفاصيل الخدمة</h2>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">الخدمة</th>
                      <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">الوصف</th>
                      <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">السعر</th>
                      <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">الكمية</th>
                      <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">الإجمالي</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {invoice.items.map((item) => (
                      <tr key={item.id}>
                        <td className="px-4 py-3">
                          <p className="font-semibold text-gray-800">{item.name}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-gray-600">{item.description}</p>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <p className="text-gray-800">{item.price.toLocaleString()} ر.س</p>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <p className="text-gray-800">{item.quantity}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-bold text-[#1E3A8A]">{item.total.toLocaleString()} ر.س</p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 5️⃣ Transactions */}
            {invoice.transactions && invoice.transactions.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
                <h2 className="text-xl font-black text-[#1E3A8A] mb-4">المعاملات</h2>

                <div className="space-y-3">
                  {invoice.transactions.map((transaction) => (
                    <div key={transaction.id} className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="font-bold text-green-800">{transaction.method}</span>
                        </div>
                        <span className="text-sm text-gray-600">{transaction.date}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600">المبلغ المسدد</p>
                          <p className="font-bold text-green-600">{transaction.amount.toLocaleString()} ر.س</p>
                        </div>
                        <div>
                          <p className="text-gray-600">الرصيد المتبقي</p>
                          <p className="font-bold text-gray-800">{transaction.balanceAfter.toLocaleString()} ر.س</p>
                        </div>
                        <div>
                          <p className="text-gray-600">تم بواسطة</p>
                          <p className="font-semibold text-gray-800">{transaction.processedBy}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 7️⃣ Audit Log */}
            <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <History className="w-5 h-5 text-[#1E3A8A]" />
                <h2 className="text-xl font-black text-[#1E3A8A]">سجل النشاطات</h2>
              </div>

              <div className="space-y-3">
                {invoice.auditLog.map((log) => (
                  <div key={log.id} className="border-r-4 border-[#1E3A8A] bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-bold text-gray-800">{log.action}</span>
                      <span className="text-sm text-gray-500">{log.date}</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">{log.details}</p>
                    <p className="text-xs text-gray-500">بواسطة: {log.user}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* 4️⃣ Financial Summary */}
            <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6 sticky top-6">
              <h2 className="text-xl font-black text-[#1E3A8A] mb-4">الملخص المالي</h2>

              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between py-2">
                  <span className="text-gray-600">الإجمالي الفرعي</span>
                  <span className="font-semibold text-gray-800">{invoice.subtotal.toLocaleString()} ر.س</span>
                </div>

                {invoice.discount > 0 && (
                  <div className="flex items-center justify-between py-2 text-green-600">
                    <span>الخصم ({invoice.discountPercentage}%)</span>
                    <span className="font-semibold">- {invoice.discount.toLocaleString()} ر.س</span>
                  </div>
                )}

                <div className="flex items-center justify-between py-2">
                  <span className="text-gray-600">الضريبة ({invoice.taxPercentage}%)</span>
                  <span className="font-semibold text-gray-800">{invoice.tax.toLocaleString()} ر.س</span>
                </div>

                <div className="border-t-2 border-gray-200 pt-3">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-gray-800">الإجمالي النهائي</span>
                    <span className="text-2xl font-black text-[#1E3A8A]">{invoice.totalAmount.toLocaleString()} ر.س</span>
                  </div>
                </div>

                <div className="border-t-2 border-gray-200 pt-3">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-gray-700">المبلغ المدفوع</span>
                    <span className="font-bold text-green-600">{invoice.paidAmount.toLocaleString()} ر.س</span>
                  </div>
                </div>

                <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-yellow-800">المتبقي</span>
                    <span className="text-xl font-black text-yellow-600">{invoice.remainingBalance.toLocaleString()} ر.س</span>
                  </div>
                </div>
              </div>

              {/* 6️⃣ Actions */}
              <div className="space-y-2 pt-4 border-t-2 border-gray-200">
                {invoice.remainingBalance > 0 && (
                  <button
                    onClick={handleRecordPayment}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-bold hover:shadow-lg transition-all"
                  >
                    <Plus className="w-5 h-5" />
                    <span>تسجيل دفعة</span>
                  </button>
                )}

                {hasPermission('finance.edit') && (
                  <button
                    onClick={handleEdit}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-[#1E3A8A] to-[#1E40AF] text-white rounded-lg font-bold hover:shadow-lg transition-all"
                  >
                    <Edit className="w-5 h-5" />
                    <span>تعديل الفاتورة</span>
                  </button>
                )}

                <button
                  onClick={handlePrint}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-50 transition-all"
                >
                  <Printer className="w-5 h-5" />
                  <span>طباعة</span>
                </button>

                <button
                  onClick={handleSendToClient}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-[#1E3A8A] text-[#1E3A8A] rounded-lg font-bold hover:bg-blue-50 transition-all"
                >
                  <Send className="w-5 h-5" />
                  <span>إرسال للعميل</span>
                </button>

                {hasPermission('finance.delete') && invoice.status === 'cancelled' && (
                  <button
                    onClick={handleDelete}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-red-600 text-red-600 rounded-lg font-bold hover:bg-red-50 transition-all"
                  >
                    <Trash2 className="w-5 h-5" />
                    <span>حذف (يحتاج صلاحية خاصة)</span>
                  </button>
                )}
              </div>

              {invoice.notes && (
                <div className="mt-4 pt-4 border-t-2 border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">ملاحظات</p>
                  <p className="text-sm text-gray-800">{invoice.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default InvoiceDetailsPage;
